import { TURNUS_EXTENSION_IDS } from "@/components/feature/Authenticaton/extensionAuthHandoff"

type Response = { opened?: boolean; error?: string }
type Runtime = {
  lastError?: { message?: string }
  sendMessage: (
    id: string,
    message: unknown,
    callback: (response?: Response) => void,
  ) => void
}
export function openCompanyHarvest(
  companyId: string,
  url: string,
): Promise<void> {
  return sendToExtension({ type: "COMPANY_INDEX_HARVEST", companyId, url })
}

export function openCompanyQueue(): Promise<void> {
  return sendToExtension({ type: "COMPANY_INDEX_QUEUE" })
}

export function openCompanyArticleHarvest(
  companyId: string,
  url: string,
  partNumbers: string[],
): Promise<void> {
  return sendToExtension({
    type: "COMPANY_INDEX_ARTICLE_HARVEST",
    companyId,
    url,
    partNumbers,
  })
}

async function sendToExtension(command: unknown): Promise<void> {
  const runtime = (window as unknown as { chrome?: { runtime?: Runtime } })
    .chrome?.runtime
  // Store builds have a stable ID. Unpacked builds deliberately omit the
  // manifest key so they can coexist, and use the app content script instead.
  const response = runtime?.sendMessage
    ? await sendToStoreBuild(runtime, command)
    : null
  if (response?.opened) return
  let result: Response
  try {
    result = await sendToUnpackedBuild(command)
  } catch (error) {
    if (response?.error) throw new Error(response.error, { cause: error })
    throw error
  }
  if (result.opened) return
  throw new Error(
    result.error || response?.error || "Unable to open the Turnus extension.",
  )
}

function sendToStoreBuild(
  runtime: Runtime,
  command: unknown,
): Promise<Response | null> {
  return new Promise((resolve) => {
    const timer = window.setTimeout(() => resolve(null), 8000)
    try {
      runtime.sendMessage(TURNUS_EXTENSION_IDS[0], command, (response) => {
        window.clearTimeout(timer)
        resolve(runtime.lastError ? null : (response ?? null))
      })
    } catch {
      window.clearTimeout(timer)
      resolve(null)
    }
  })
}

function sendToUnpackedBuild(command: unknown): Promise<Response> {
  return requestContentScript(
    "TURNUS_COMPANY_INDEX_BRIDGE_DISCOVER",
    "TURNUS_COMPANY_INDEX_BRIDGE_DISCOVER_RESPONSE",
    {},
  ).then((discovered) => {
    if (!/^[a-p]{32}$/.test(discovered.extensionId || ""))
      throw new Error("The Turnus extension did not identify itself.")
    return requestContentScript(
      "TURNUS_COMPANY_INDEX_BRIDGE",
      "TURNUS_COMPANY_INDEX_BRIDGE_RESPONSE",
      { targetExtensionId: discovered.extensionId, command },
    ).then((answer) => answer.response || {})
  })
}

function requestContentScript(
  type: string,
  responseType: string,
  payload: Record<string, unknown>,
): Promise<{ extensionId?: string; response?: Response }> {
  return new Promise((resolve, reject) => {
    const requestId = crypto.randomUUID()
    const origin = window.location.origin
    const timer = window.setTimeout(() => {
      window.removeEventListener("message", onMessage)
      reject(
        new Error(
          "Load the updated Turnus Chrome extension, sign in, and select the same environment as this app.",
        ),
      )
    }, 2000)
    function onMessage(event: MessageEvent) {
      if (
        event.source !== window ||
        event.origin !== origin ||
        event.data?.type !== responseType ||
        event.data?.requestId !== requestId
      )
        return
      window.clearTimeout(timer)
      window.removeEventListener("message", onMessage)
      resolve(event.data)
    }
    window.addEventListener("message", onMessage)
    window.postMessage({ type, requestId, ...payload }, origin)
  })
}
